import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap'

import SalesUsersApi from 'api/admins/sales-users-api'
import {
  buildAddressAttributes,
  buildDetailAttributes,
  buildNestedAttributesForSave,
} from 'domains/sales/users/forms/user-form/sales-user-payload'
import SalesUsersDetailForm from 'domains/sales/users/forms/detail-form/sales-users-detail-form'
import SalesUsersAddressForm from 'domains/sales/users/forms/address-form/sales-users-address-form'

const MAX_KYC_MB = 5
const MAX_KYC_BYTES = MAX_KYC_MB * 1024 * 1024
const ALLOWED_KYC_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf']
const DEFAULT_PHONE_CODE_ID = 62

const validateKycFile = (file) => {
  if (!file) return 'File tidak ditemukan.'
  const type = (file.type || '').toLowerCase()
  if (!ALLOWED_KYC_TYPES.includes(type)) return 'Format file tidak didukung. Gunakan JPG/JPEG/PNG/PDF.'
  if (file.size > MAX_KYC_BYTES) return `Ukuran file terlalu besar. Maksimal ${MAX_KYC_MB}MB.`
  return null
}

const safeRevoke = (url) => {
  try {
    if (url && String(url).startsWith('blob:')) URL.revokeObjectURL(url)
  } catch (_) {}
}

const hasIdCardValue = (d) => {
  return !!(d?.id_card_signed_id || d?.id_card?.url || d?.id_card?.filename)
}

const isBlankDetail = (d) => {
  const name = (d?.name || '').trim()
  const phone = (d?.phone || '').trim()
  const bank = (d?.bank_account_number || '').trim()
  const idCard = hasIdCardValue(d)
  return !name && !phone && !bank && !idCard
}

const isBlankAddress = (a) => {
  const rn = (a?.recipient_name || '').trim()
  const rnum = (a?.recipient_number || '').trim()
  const ca = (a?.complete_address || '').trim()
  const ins = (a?.instruction || '').trim()
  return !rn && !rnum && !ca && !ins
}


const normalizeDetailSearchValue = (value) => String(value || '').trim().toLowerCase()

const normalizePhoneSearchValue = (value) => String(value || '').replace(/\D/g, '')

const buildDetailPhoneSearchValues = (detail) => {
  const rawPhone = normalizePhoneSearchValue(detail?.phone)
  const localPhone = rawPhone.replace(/^62/, '').replace(/^0+/, '')
  const values = [rawPhone, localPhone]

  if (localPhone) {
    values.push(`0${localPhone}`)
    values.push(`62${localPhone}`)
  }

  return [...new Set(values.filter(Boolean))]
}

const detailMatchesSearch = (detail, keyword) => {
  const q = normalizeDetailSearchValue(keyword)
  if (!q) return true

  const qDigits = normalizePhoneSearchValue(q)
  const searchableText = [detail?.name, detail?.phone, detail?.label]
    .map(normalizeDetailSearchValue)
    .filter(Boolean)
    .join(' ')

  if (searchableText.includes(q)) return true
  if (!qDigits) return false

  return buildDetailPhoneSearchValues(detail).some((phone) => phone.includes(qDigits))
}

const sortDetailsDesc = (rawDetails = []) => {
  const list = Array.isArray(rawDetails) ? rawDetails.filter(Boolean) : []

  return list.slice().sort((a, b) => {
    const ta = a?.created_at ? new Date(a.created_at).getTime() : 0
    const tb = b?.created_at ? new Date(b.created_at).getTime() : 0
    if (ta !== tb) return tb - ta
    return Number(b?.id || 0) - Number(a?.id || 0)
  })
}

const normalizeDetails = (rawDetails = []) => {
  const list = sortDetailsDesc(rawDetails)

  const withDestroy = list.map((d) => ({
    ...d,
    phone_code_id:
      d?.phone_code_id === null || d?.phone_code_id === undefined || d?.phone_code_id === ''
        ? DEFAULT_PHONE_CODE_ID
        : d.phone_code_id,
    _destroy: '0',
  }))

  if (withDestroy.length === 0) {
    return [{ primary: true, phone_code_id: DEFAULT_PHONE_CODE_ID, _destroy: '0' }]
  }

  let foundPrimary = false
  const normalized = withDestroy.map((d) => {
    const alive = String(d._destroy || '0') !== '1'
    if (!alive) return d

    if (d.primary && !foundPrimary) {
      foundPrimary = true
      return { ...d, primary: true }
    }

    if (d.primary && foundPrimary) {
      return { ...d, primary: false }
    }

    return d
  })

  if (!normalized.some((d) => String(d._destroy || '0') !== '1' && d.primary)) {
    const firstAlive = normalized.findIndex((d) => String(d._destroy || '0') !== '1')
    if (firstAlive >= 0) normalized[firstAlive] = { ...normalized[firstAlive], primary: true }
  }

  return normalized
}

const normalizeAddresses = (rawAddresses = []) => {
  const list = Array.isArray(rawAddresses) ? rawAddresses : []
  return list.map((a) => ({ ...a, _destroy: '0' }))
}

const SalesUsersForm = ({ mode, user, submitting, onSubmit, onEmailChange, renderBankVerification, renderBankAccounts, saveActionRef }) => {
  const [email, setEmail] = useState('')
  const [birthdate, setBirthdate] = useState('')
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [promoOptIn, setPromoOptIn] = useState(false)

  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false)

  const [details, setDetails] = useState([{ primary: true, phone_code_id: DEFAULT_PHONE_CODE_ID, _destroy: '0' }])
  const [detailSearchInput, setDetailSearchInput] = useState('')
  const [detailSearchKeyword, setDetailSearchKeyword] = useState('')
  const [addresses, setAddresses] = useState([])

  // per-detail upload state (index-based)
  const [uploadingIdCard, setUploadingIdCard] = useState({})

  // only one address pinpoint open at a time
  const [openAddressPinpointIndex, setOpenAddressPinpointIndex] = useState(null)

  // keep latest details for cleanup on unmount
  const detailsRef = useRef(details)
  useEffect(() => {
    detailsRef.current = details
  }, [details])

  // cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        detailsRef.current.forEach((d) => safeRevoke(d?.id_card_local_preview_url))
      } catch (_) {}
    }
  }, [])

  useEffect(() => {
    // cleanup previews from previous loaded user (important when switching user / after save re-render)
    try {
      details.forEach((d) => safeRevoke(d?.id_card_local_preview_url))
    } catch (_) {}

    if (!user) {
      setEmail('')
      setBirthdate('')
      setAgreeTerms(false)
      setPromoOptIn(false)
      setPassword('')
      setPasswordConfirmation('')
      setDetails([{ primary: true, phone_code_id: DEFAULT_PHONE_CODE_ID, _destroy: '0' }])
      setDetailSearchInput('')
      setDetailSearchKeyword('')
      setAddresses([])
      setOpenAddressPinpointIndex(null)
      setUploadingIdCard({})
      if (onEmailChange) onEmailChange('')
      return
    }

    setEmail(user.email || '')
    if (onEmailChange) onEmailChange(user.email || '')

    setBirthdate(user.birthdate || '')
    setAgreeTerms(!!user.agree_terms)
    setPromoOptIn(!!user.promo_opt_in)

    setDetails(normalizeDetails(user.details || []))
    setDetailSearchInput('')
    setDetailSearchKeyword('')

    const addrList = user.addresses || (user.address ? [user.address] : [])
    setAddresses(normalizeAddresses(addrList))
    setOpenAddressPinpointIndex(null)
    setUploadingIdCard({})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const aliveDetails = useMemo(() => details.filter((d) => String(d._destroy || '0') !== '1'), [details])

  const visibleDetailEntries = useMemo(
    () =>
      details
        .map((detail, index) => ({ detail, index }))
        .filter(({ detail }) => detailMatchesSearch(detail, detailSearchKeyword)),
    [details, detailSearchKeyword]
  )

  const applyDetailSearch = (event) => {
    if (event) event.preventDefault()
    setDetailSearchKeyword(detailSearchInput.trim())
  }

  const clearDetailSearch = () => {
    setDetailSearchInput('')
    setDetailSearchKeyword('')
  }

  const addDetail = () => {
    setDetails((prev) => {
      const newDetail = { primary: false, phone_code_id: DEFAULT_PHONE_CODE_ID, _destroy: '0' }
      const next = [newDetail, ...prev]

      if (!next.some((d) => String(d._destroy || '0') !== '1' && d.primary)) {
        const firstAlive = next.findIndex((d) => String(d._destroy || '0') !== '1')
        if (firstAlive >= 0) next[firstAlive] = { ...next[firstAlive], primary: true }
      }

      return next
    })
  }

  const changeDetail = (idx, nextDetail) => {
    setDetails((prev) => prev.map((d, i) => (i === idx ? nextDetail : d)))
  }

  const makePrimary = (idx) => {
    setDetails((prev) =>
      prev.map((d, i) => {
        if (String(d._destroy || '0') === '1') return d
        return { ...d, primary: i === idx }
      })
    )
  }

  const removeDetail = (idx) => {
    setDetails((prev) => {
      const d = prev[idx]

      // cleanup preview URL
      safeRevoke(d?.id_card_local_preview_url)

      if (d?.id) {
        const next = prev.map((x, i) => (i === idx ? { ...x, _destroy: '1', primary: false } : x))
        const alive = next.filter((x) => String(x._destroy || '0') !== '1')
        if (alive.length > 0 && !alive.some((x) => x.primary)) {
          const firstAliveIdx = next.findIndex((x) => String(x._destroy || '0') !== '1')
          if (firstAliveIdx >= 0) next[firstAliveIdx] = { ...next[firstAliveIdx], primary: true }
        }
        return next
      }

      const next = prev.slice()
      next.splice(idx, 1)

      const alive = next.filter((x) => String(x._destroy || '0') !== '1')
      if (alive.length > 0 && !alive.some((x) => x.primary)) {
        const firstAliveIdx = next.findIndex((x) => String(x._destroy || '0') !== '1')
        if (firstAliveIdx >= 0) next[firstAliveIdx] = { ...next[firstAliveIdx], primary: true }
      }

      return next.length ? next : [{ primary: true, phone_code_id: DEFAULT_PHONE_CODE_ID, _destroy: '0' }]
    })
  }

  const undoRemoveDetail = (idx) => {
    setDetails((prev) => {
      const next = prev.map((d, i) => (i === idx ? { ...d, _destroy: '0' } : d))
      const alive = next.filter((x) => String(x._destroy || '0') !== '1')
      if (alive.length > 0 && !alive.some((x) => x.primary)) {
        const firstAliveIdx = next.findIndex((x) => String(x._destroy || '0') !== '1')
        if (firstAliveIdx >= 0) next[firstAliveIdx] = { ...next[firstAliveIdx], primary: true }
      }
      return next
    })
  }

  const addAddress = () => {
    setOpenAddressPinpointIndex(null)
    setAddresses((prev) => [...prev, { _destroy: '0' }])
  }

  const changeAddress = (idx, nextAddr) => {
    setAddresses((prev) => prev.map((a, i) => (i === idx ? nextAddr : a)))
  }

  const setAddressCoordinates = (idx, coords) => {
    setAddresses((prev) =>
      prev.map((a, i) =>
        i === idx
          ? {
              ...a,
              latitude: coords?.latitude ?? null,
              longitude: coords?.longitude ?? null,
            }
          : a
      )
    )
  }

  const removeAddress = (idx) => {
    setOpenAddressPinpointIndex(null)

    setAddresses((prev) => {
      const a = prev[idx]
      if (a?.id) return prev.map((x, i) => (i === idx ? { ...x, _destroy: '1' } : x))
      const next = prev.slice()
      next.splice(idx, 1)
      return next
    })
  }

  const undoRemoveAddress = (idx) => {
    setAddresses((prev) => prev.map((a, i) => (i === idx ? { ...a, _destroy: '0' } : a)))
  }

  // upload handler (per detail)
  const uploadIdCardForDetail = async (idx, file) => {
    const err = validateKycFile(file)
    if (err) {
      window.alert(err)
      return
    }

    const d = details[idx]
    const prevPreviewUrl = d?.id_card_local_preview_url

    setUploadingIdCard((prev) => ({ ...prev, [idx]: true }))

    const localPreviewUrl = file.type?.toLowerCase().startsWith('image/') ? URL.createObjectURL(file) : null

    try {
      // existing user + existing detail => attach langsung
      if (user?.id && d?.id) {
        const res = await SalesUsersApi.uploadDetailIdCard({ userId: user.id, detailId: d.id, file })
        const idCard = res?.data?.id_card || null

        setDetails((prev) =>
          prev.map((x, i) =>
            i === idx ? { ...x, id_card: idCard, id_card_signed_id: undefined, id_card_local_preview_url: localPreviewUrl } : x
          )
        )

        safeRevoke(prevPreviewUrl)
        return
      }

      // create mode / detail baru => preupload blob, attach saat Save via signed_id
      const res = await SalesUsersApi.preuploadKycDocument({ file })
      const signedId = res?.data?.blob_signed_id
      const meta = res?.data?.file || null

      setDetails((prev) =>
        prev.map((x, i) =>
          i === idx ? { ...x, id_card: meta, id_card_signed_id: signedId, id_card_local_preview_url: localPreviewUrl } : x
        )
      )

      safeRevoke(prevPreviewUrl)
    } catch (e) {
      safeRevoke(localPreviewUrl)
      window.alert(e?.response?.data?.message || e?.message || 'Upload dokumen gagal.')
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      setUploadingIdCard((prev) => ({ ...prev, [idx]: false }))
    }
  }

  const removeIdCardForDetail = async (idx) => {
    const d = details[idx]
    if (!window.confirm("Delete KTP / dokumen ini? Bukti pengajuan pembayaran sebelumnya tetap tersimpan. Upload dan Save rekening kembali jika mengganti dokumen.")) return
    setUploadingIdCard((prev) => ({ ...prev, [idx]: true }))

    try {
      if (user?.id && d?.id) {
        await SalesUsersApi.deleteDetailIdCard({ userId: user.id, detailId: d.id })
      }

      safeRevoke(d?.id_card_local_preview_url)

      setDetails((prev) =>
        prev.map((x, i) =>
          i === idx ? { ...x, id_card: null, id_card_signed_id: undefined, id_card_local_preview_url: undefined } : x
        )
      )
    } catch (e) {
      window.alert(e?.response?.data?.message || e?.message || 'Hapus dokumen gagal.')
      // eslint-disable-next-line no-console
      console.error(e)
    } finally {
      setUploadingIdCard((prev) => ({ ...prev, [idx]: false }))
    }
  }

  const cleanedDetailAttributes = () =>
    details
      .filter((d) => {
        if (String(d._destroy || '0') === '1') return true
        if (d?.id) return true
        return !isBlankDetail(d)
      })
      .map(buildDetailAttributes)

  const cleanedAddressAttributes = () =>
    addresses
      .filter((a) => {
        if (String(a._destroy || '0') === '1') return true
        if (a?.id) return true
        return !isBlankAddress(a)
      })
      .map(buildAddressAttributes)

  const buildPayload = () => {
    const allDetails = cleanedDetailAttributes()
    const allAddresses = cleanedAddressAttributes()

    const originalDetails = normalizeDetails(user?.details || [])
      .filter((d) => d?.id || !isBlankDetail(d))
      .map(buildDetailAttributes)

    const originalAddressRows = user?.addresses || (user?.address ? [user.address] : [])
    const originalAddresses = normalizeAddresses(originalAddressRows)
      .filter((a) => a?.id || !isBlankAddress(a))
      .map(buildAddressAttributes)

    const nestedAttributes = buildNestedAttributesForSave({
      mode,
      details: allDetails,
      addresses: allAddresses,
      originalDetails,
      originalAddresses,
    })

    return {
      email,
      birthdate: birthdate || null,
      agree_terms: !!agreeTerms,
      promo_opt_in: !!promoOptIn,
      ...nestedAttributes,
    }
  }

  const validateBeforeSubmit = (payload) => {
    if (!payload.email?.trim()) return 'Email is required.'

    if (mode === 'create') {
      if (!password) return 'Password is required for new user.'
      if (password !== passwordConfirmation) return 'Password confirmation does not match.'
      payload.password = password
      payload.password_confirmation = passwordConfirmation
    }

    // Validate the complete form state. In edit mode the request payload
    // intentionally contains only changed nested records.
    const allDetails = cleanedDetailAttributes()
    const alive = allDetails.filter((d) => String(d._destroy || '0') !== '1')
    if (alive.length === 0) return 'At least one detail is required.'
    if (!alive.some((d) => !!d.primary)) return 'At least one primary detail is required.'

    const aliveNewOrExisting = alive.filter((d) => d.id || !isBlankDetail(d))
    if (aliveNewOrExisting.length === 0) {
      return 'Please fill at least one detail (name / phone / bank account number / id card).'
    }

    return null
  }

  const handleSubmit = async () => {
    const payload = buildPayload()
    const err = validateBeforeSubmit(payload)
    if (err) {
      window.alert(err)
      return
    }

    await onSubmit(payload)
  }
  useEffect(() => {
    const revealEvidence = () => {
      setDetailSearchInput('')
      setDetailSearchKeyword('')
      window.requestAnimationFrame(() => {
        const target = document.querySelector('[data-primary-evidence="true"]') || document.querySelector('[id^="user-detail-evidence-"]')
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        target?.querySelector('input[type="file"]')?.focus({ preventScroll: true })
      })
    }
    window.addEventListener('settlement-show-primary-evidence', revealEvidence)
    return () => window.removeEventListener('settlement-show-primary-evidence', revealEvidence)
  }, [])

  if (saveActionRef) saveActionRef.current = handleSubmit

  const bankVerificationDisabled =
    submitting ||
    Object.values(uploadingIdCard).some(Boolean) ||
    JSON.stringify(details) !== JSON.stringify(normalizeDetails(user?.details || [])) ||
    JSON.stringify(addresses) !== JSON.stringify(normalizeAddresses(user?.addresses || (user?.address ? [user.address] : []))) ||
    birthdate !== (user?.birthdate || '') ||
    agreeTerms !== !!user?.agree_terms ||
    promoOptIn !== !!user?.promo_opt_in

  return (
    <div>
      <h3 className="text-uppercase mb-3">Profile Section</h3>
      <Row form>
        <Col md="6">
          <FormGroup>
            <Label>Email</Label>
            <Input
              value={email}
              disabled={mode === 'edit'}
              onChange={(e) => {
                const v = e.target.value
                setEmail(v)
                if (onEmailChange) onEmailChange(v)
              }}
              placeholder="email@domain.com"
            />
            {mode === 'edit' ? (
              <div className="text-muted small mt-1">
                User email is read-only after account creation.
              </div>
            ) : null}
          </FormGroup>
        </Col>

        <Col md="6">
          <FormGroup>
            <Label>Birthdate (YYYY-MM-DD)</Label>
            <Input value={birthdate || ''} onChange={(e) => setBirthdate(e.target.value)} placeholder="1998-12-31" />
          </FormGroup>
        </Col>
      </Row>

      {mode === 'create' ? (
        <Row form>
          <Col md="6">
            <FormGroup>
              <Label>Password</Label>
              <div className="position-relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  style={{ paddingRight: '42px' }}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 0,
                    color: '#6c757d',
                  }}
                >
                  <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </FormGroup>
          </Col>

          <Col md="6">
            <FormGroup>
              <Label>Password Confirmation</Label>
              <div className="position-relative">
                <Input
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  autoComplete="new-password"
                  style={{ paddingRight: '42px' }}
                />

                <button
                  type="button"
                  onClick={() => setShowPasswordConfirmation((prev) => !prev)}
                  aria-label={showPasswordConfirmation ? 'Hide password confirmation' : 'Show password confirmation'}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    padding: 0,
                    color: '#6c757d',
                  }}
                >
                  <i className={`fa ${showPasswordConfirmation ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
            </FormGroup>
          </Col>
        </Row>
      ) : null}

      <Row className="mb-3">
        <Col md="6">
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)} /> Agree Terms
            </Label>
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup check>
            <Label check>
              <Input type="checkbox" checked={promoOptIn} onChange={(e) => setPromoOptIn(e.target.checked)} /> Promo Opt-In
            </Label>
          </FormGroup>
        </Col>
      </Row>

      {mode === 'create' && <div className="text-right mt-4"><Button color="primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</Button></div>}

      <hr />

      <div className="d-flex justify-content-between align-items-center">
        <div>
          <h3 className="text-uppercase mb-0">Detail Section</h3>
          <div className="text-muted small mt-1">
            Showing {visibleDetailEntries.length} of {details.length} details
          </div>
        </div>
        <Button color="success" onClick={addDetail}>
          + Add Detail
        </Button>
      </div>

      <form onSubmit={applyDetailSearch}>
        <Row form className="mt-3">
          <Col md="6">
            <FormGroup>
              <Label>Search User Detail Name / Phone</Label>
              <div className="d-flex" style={{ gap: '8px' }}>
                <Input
                  value={detailSearchInput}
                  onChange={(e) => setDetailSearchInput(e.target.value)}
                  placeholder="Search detail name or phone..."
                />
                <Button color="primary" type="submit">
                  Search
                </Button>
                {detailSearchKeyword ? (
                  <Button color="secondary" type="button" outline onClick={clearDetailSearch}>
                    Clear
                  </Button>
                ) : null}
              </div>
              <div className="text-muted small mt-1">
                Press Enter or click Search to filter visible detail cards. This does not delete or modify saved data.
              </div>
            </FormGroup>
          </Col>
        </Row>
      </form>

      {visibleDetailEntries.length === 0 ? (
        <div className="text-muted mt-2">No detail matches the current search.</div>
      ) : (
        visibleDetailEntries.map(({ detail: d, index: idx }) => (
          <SalesUsersDetailForm
            key={d.id || `detail-${idx}`}
            detail={d}
            onSave={handleSubmit}
            saving={submitting || Object.values(uploadingIdCard).some(Boolean)}
            bankVerification={renderBankVerification ? renderBankVerification(d, bankVerificationDisabled) : null}
            index={idx}
            displayNumber={details.length - idx}
            canRemove={aliveDetails.length > 1}
            onChange={changeDetail}
            onMakePrimary={makePrimary}
            onRemove={removeDetail}
            onUndoRemove={undoRemoveDetail}
            uploadingIdCard={!!uploadingIdCard[idx]}
            onUploadIdCard={uploadIdCardForDetail}
            onRemoveIdCard={removeIdCardForDetail}
          />
        ))
      )}

      <hr />

      {renderBankAccounts && renderBankAccounts()}

      <div className="d-flex justify-content-between align-items-center">
        <h3 className="text-uppercase mb-0">Address Section</h3>
        <Button color="success" onClick={addAddress}>
          + Add Address
        </Button>
      </div>

      {addresses.length === 0 ? (
        <div className="text-muted mt-2">No addresses yet.</div>
      ) : (
        addresses.map((a, idx) => (
          <SalesUsersAddressForm
            key={a.id || `addr-${idx}`}
            address={a}
            index={idx}
            canRemove={addresses.filter((x) => String(x._destroy || '0') !== '1').length > 0}
            onChange={changeAddress}
            onRemove={removeAddress}
            onUndoRemove={undoRemoveAddress}
            pinpointOpen={openAddressPinpointIndex === idx}
            onOpenPinpoint={() => setOpenAddressPinpointIndex(idx)}
            onClosePinpoint={() => setOpenAddressPinpointIndex((current) => (current === idx ? null : current))}
            onSubmitCoordinates={(coords) => setAddressCoordinates(idx, coords)}
          />
        ))
      )}

      {mode === 'create' && <div className="text-right mt-4"><Button color="primary" onClick={handleSubmit} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</Button></div>}

    </div>
  )
}

SalesUsersForm.propTypes = {
  saveActionRef: PropTypes.object,
  renderBankAccounts: PropTypes.func,
  renderBankVerification: PropTypes.func,
  mode: PropTypes.oneOf(['create', 'edit']).isRequired,
  user: PropTypes.object,
  submitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onEmailChange: PropTypes.func,
}

SalesUsersForm.defaultProps = {
  user: null,
  submitting: false,
  onEmailChange: null,
}

export default SalesUsersForm
